import type { Component, DefineComponent } from "vue";

/**
 * Defines how a sidepanel should behave when opened while another is currently displayed
 */
export enum SidePanelInterruptBehaviour {
	/** Wait for current panel to close, then be first in queue */
	WAIT = 'wait',
	/** Hide current panel, show this one, restore current after closing */
	HIDE = 'hide',
	/** Render on top of current panel (both visible with stacked z-indexes) */
	OVERLAY = 'overlay',
	/** Replace current panel (move it back to queue if persistent) */
	REPLACE = 'replace'
}

export interface SidePanelConfig {
	id: string;
	component: Component | DefineComponent | string;
	props?: Record<string, any>;
	priority: number;
	dismissable?: boolean;
	persistent?: boolean;
	onClose?: () => void;  // Called immediately when closing begins (before animation)
	onClosed?: () => void;  // Called after panel is fully closed (after animation completes)
	onOpen?: () => void;
	onHide?: () => void;  // Called when temporarily hidden by another panel
	onRestore?: () => void;  // Called when restored after being hidden
	title?: string;
	size?: 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge' | 'fullscreen';
	fullscreen?: boolean;
	showCloseButton?: boolean;
	hideHeader?: boolean;
	closeOnBackdropClick?: boolean;
	preventScroll?: boolean;
	zIndex?: number;
	headerStyle?: Record<string, string>;
	forceNonFullscreen?: boolean;
	noPad?: boolean;
	SidePanelInterruptBehaviour?: SidePanelInterruptBehaviour;
	hiddenBy?: string;  // Track which panel hid this one (for restoration)
}

/**
 * Properties that can be safely updated after panel creation.
 * Excludes: id, component, callbacks, and internal tracking properties.
 */
export type UpdatablePanelConfig = Partial<Pick<SidePanelConfig,
	| 'title'
	| 'size'
	| 'fullscreen'
	| 'showCloseButton'
	| 'hideHeader'
	| 'closeOnBackdropClick'
	| 'preventScroll'
	| 'zIndex'
	| 'dismissable'
	| 'props'
	| 'headerStyle'
>>;
